import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { I18nProvider } from './context/I18nContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { getUserKey } from './utils/userStorage';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import ProjectList from './pages/ProjectList';
import Analytics from './pages/Analytics';
import Calendar from './pages/Calendar';
import ContactUs from './pages/ContactUs';
import Profile from './pages/Profile';
import AdminPanel from './pages/AdminPanel';
import Login from './pages/Login';
import GlobalSearch from './components/GlobalSearch';
import ProtectedRoute from './components/ProtectedRoute';
import useIsMobile from './hooks/useIsMobile';
import { FiUser } from 'react-icons/fi';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

function AppContent({ isAuthenticated, setIsAuthenticated }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [profileInfo, setProfileInfo] = useState({ name: '', email: '', status: '', avatar: '', accent: 'blue' });
  const showSidebar = isAuthenticated && location.pathname !== '/login';

  const loadProfileValue = (key, fallback) => {
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : fallback;
    } catch {
      return fallback;
    }
  };

  const { clearRole, currentUser, currentRole } = useAuth();

  useEffect(() => {
    if (!isAuthenticated || !currentUser?.id || currentUser.id === 'admin') return;

    return onSnapshot(doc(db, 'smm_users', currentUser.id), (snapshot) => {
      if (snapshot.exists()) return;

      try { localStorage.removeItem('smm_crm_authenticated'); } catch {}
      setIsAuthenticated(false);
      clearRole();
      navigate('/login', { replace: true });
    });
  }, [isAuthenticated, currentUser?.id, clearRole, navigate, setIsAuthenticated]);

  const refreshProfileInfo = useCallback(() => {
    if (currentUser) {
      setProfileInfo({
        name: currentUser.name || 'User',
        email: currentUser.email || '',
        status: currentUser.role || currentRole,
        avatar: loadProfileValue(getUserKey('avatar'), ''),
        accent: loadProfileValue(getUserKey('accent'), 'blue'),
      });
    } else {
      setProfileInfo({
        name: loadProfileValue(getUserKey('name'), 'Admin'),
        email: loadProfileValue(getUserKey('email'), ''),
        status: loadProfileValue(getUserKey('status'), 'Admin'),
        avatar: loadProfileValue(getUserKey('avatar'), ''),
        accent: loadProfileValue(getUserKey('accent'), 'blue'),
      });
    }
  }, [currentUser, currentRole]);

  useEffect(() => {
    refreshProfileInfo();
    const handler = () => refreshProfileInfo();
    window.addEventListener('profileUpdated', handler);
    return () => window.removeEventListener('profileUpdated', handler);
  }, [refreshProfileInfo]);

  const openSearch = () => setShowSearch(true);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); setShowSearch(true); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const openLogoutConfirm = () => setShowLogoutConfirm(true);
  const closeLogoutConfirm = () => setShowLogoutConfirm(false);
  const handleLogout = () => {
    setIsAuthenticated(false);
    try { localStorage.removeItem('smm_crm_authenticated'); } catch {}
    clearRole();
    navigate('/login');
  };

  const accentColor = profileInfo.accent === 'purple' ? '#8b5cf6' : profileInfo.accent === 'green' ? '#16a34a' : profileInfo.accent === 'orange' ? '#f97316' : '#2563eb';
  const accentRgb = profileInfo.accent === 'purple' ? '139, 92, 246' : profileInfo.accent === 'green' ? '22, 163, 74' : profileInfo.accent === 'orange' ? '249, 115, 22' : '37, 99, 235';

  useEffect(() => {
    try {
      document.documentElement.style.setProperty('--accent-color', accentColor);
      document.documentElement.style.setProperty('--accent-color-rgb', accentRgb);
    } catch (e) {
      // ignore
    }
  }, [accentColor, accentRgb]);

  const isLoginPage = location.pathname === '/login';

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {showSidebar && <Sidebar openLogoutConfirm={openLogoutConfirm} openSearch={openSearch} />}
      <main style={{
        flex: 1,
        padding: isLoginPage ? 0 : isMobile ? '12px' : '18px',
        paddingBottom: isLoginPage ? 0 : isMobile && showSidebar ? '80px' : isMobile ? '12px' : '18px',
        minWidth: 0,
        minHeight: '100vh'
      }}>
        <Routes>
          {/* Kirish sahifasi: Agar kiritilgan bo'lsa, / ga yuborish */}
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to="/" /> : <Login setIsAuthenticated={setIsAuthenticated} />} 
          />
          
          {/* Himoyalangan sahifalar */}
          <Route path="/" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Dashboard /></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute isAuthenticated={isAuthenticated}><ProjectList /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Analytics /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Calendar /></ProtectedRoute>} />
          <Route path="/contact" element={<ProtectedRoute isAuthenticated={isAuthenticated}><ContactUs /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute isAuthenticated={isAuthenticated}><AdminPanel /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Profile /></ProtectedRoute>} />
          
          {/* Noto'g'ri yo'llarni bosh sahifaga yo'naltirish */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>

      {showSidebar && !isMobile && location.pathname !== '/projects' && !location.pathname.startsWith('/analytics') && !location.pathname.startsWith('/profile') && !location.pathname.startsWith('/admin') && !location.pathname.startsWith('/calendar') && !location.pathname.startsWith('/contact') && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 50, width: '260px', maxWidth: 'calc(100vw - 32px)', borderRadius: '22px', padding: '16px', background: 'rgba(15, 23, 42, 0.92)', color: '#fff', backdropFilter: 'blur(16px)', boxShadow: '0 24px 60px rgba(0, 0, 0, 0.22)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={() => navigate('/profile')} style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '16px', overflow: 'hidden', border: `2px solid ${accentColor}`, background: '#0f172a', display: 'grid', placeItems: 'center' }}>
              {profileInfo.avatar ? (
                <img src={profileInfo.avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <FiUser size={19} aria-hidden="true" />
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '15px', fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{profileInfo.name}</p>
              <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{profileInfo.email}</p>
            </div>
          </button>
        </div>
      )}

      {showSearch && <GlobalSearch onClose={() => setShowSearch(false)} />}

      {showLogoutConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={closeLogoutConfirm} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)' }} />
          <div style={{ position: 'relative', width: '360px', maxWidth: '90%', background: '#0f172a', borderRadius: '20px', padding: '30px', boxShadow: '0 24px 60px rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.08)', zIndex: 10000, color: '#fff' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: '20px', fontWeight: 'bold' }}>Chiqishni tasdiqlang</h3>
            <p style={{ margin: '0 0 24px', color: '#cbd5e1', lineHeight: 1.6 }}>Tizimdan chiqishni xohlaysizmi? Iltimos, tasdiqlang.</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={closeLogoutConfirm} style={{ flex: 1, padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(148,163,184,0.25)', background: 'rgba(255,255,255,0.06)', color: '#f8fafc', cursor: 'pointer', fontWeight: '700' }}>Yoq</button>
              <button onClick={() => { closeLogoutConfirm(); handleLogout(); }} style={{ flex: 1, padding: '12px 16px', borderRadius: '12px', border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: '700' }}>Ha</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      return localStorage.getItem('smm_crm_authenticated') === 'true';
    } catch {
      return false;
    }
  });

  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <Router>
            <AppContent isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} />
          </Router>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;